import { useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Clock, Loader2 } from 'lucide-react';
import { formatCurrencyNoDecimals, formatPercentage } from '@/utils/formatters';
import { useHoldings } from '../hooks/useHoldings';
import { FundHoldingCard } from '@/portal/dashboard/components/FundHoldingCard';
import { InformationAsOfLabel } from '@/portal/shared/components';

function HoldingsLoading() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Holdings</h1>
        <p className="text-muted-foreground mt-1">
          Your fund commitments and portfolio companies
        </p>
      </div>

      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Holdings</h1>
        <p className="text-muted-foreground mt-1">
          Your fund commitments and portfolio companies
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No Holdings Found
          </h3>
          <p className="text-muted-foreground text-center max-w-md">
            You don&apos;t have any fund commitments yet. Contact your fund
            manager for more information about investment opportunities.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ComingSoonState() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Holdings</h1>
        <p className="text-muted-foreground mt-1">
          Your fund commitments and portfolio companies
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Clock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Coming Soon
          </h3>
          <p className="text-muted-foreground text-center max-w-md">
            Your fund manager hasn&apos;t published holdings information yet.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function PortalHoldingsPage() {
  const navigate = useNavigate();
  const { data: holdings, isLoading, error } = useHoldings();

  const handleFundDetails = (fundId: string) => {
    navigate(`/portal/holdings/funds/${fundId}`);
  };

  if (isLoading) {
    return <HoldingsLoading />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Holdings</h1>
          <p className="text-muted-foreground mt-1">
            Your fund commitments and portfolio companies
          </p>
        </div>

        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Error Loading Holdings
            </h3>
            <p className="text-muted-foreground text-center max-w-md">
              There was a problem loading your holdings data. Please try again
              later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (holdings?.fund_count === 0 && holdings.hidden_fund_count > 0) {
    return <ComingSoonState />;
  }

  if (!holdings || holdings.fund_count === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Holdings</h1>
          <p className="text-muted-foreground mt-1">
            Your fund commitments and portfolio companies
          </p>
        </div>
        <InformationAsOfLabel
          date={holdings.information_as_of}
          className="mt-2"
        />
      </div>

      {/* Holdings Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Total Commitments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyNoDecimals(holdings.total_commitment)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {holdings.fund_count}{' '}
              {holdings.fund_count === 1 ? 'fund' : 'funds'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Capital Called
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyNoDecimals(holdings.total_called)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {holdings.total_commitment > 0
                ? formatPercentage(
                    Number(holdings.total_called) /
                      Number(holdings.total_commitment)
                  )
                : '0.0%'}{' '}
              of commitment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Prepayment Pool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyNoDecimals(holdings.total_prepayment_pool)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Available balance across all funds
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Fund Holdings Detail */}
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Fund Commitments</h2>
        </div>
        <div className="space-y-6">
          {holdings.funds.map((fund) => (
            <FundHoldingCard
              key={fund.id}
              fund={fund}
              onViewDetails={handleFundDetails}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
